import React, { useCallback } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import _ from 'lodash';

import { useAppSelector, useGetVirtualListProps, useTranslation, useWaitingList } from 'client/hooks';
import { selectUserRoomRole } from 'client/redux/client/selectors';
import { PubnubMsgsItem } from 'client/redux/pubnub/types';
import { selectRoomId } from 'client/redux/room/selectors';
import { USER_ROLES } from 'constants/index';

import { Waiter } from './Waiter';

import css from './WaitersList.module.scss';

const WaitersList = () => {
  const { translate } = useTranslation('room');
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const waitingList = useWaitingList();
  const roomId = useAppSelector(selectRoomId);
  const userRole = useAppSelector((state) => selectUserRoomRole(state, roomId));
  const { columnsAmount, rowsAmount, getItemIndex, getColumnWidth, getRowHeight } =
    useGetVirtualListProps<PubnubMsgsItem>({
      list: waitingList,
    });

  const onItemControlPopUpClose = () => {
    _.defer(() => {
      setActiveId(null);
    });
  };

  const onItemClick = useCallback(
    (itemId: string | undefined) => {
      if ((userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.HOST) && itemId) {
        setActiveId(itemId);
      }
    },
    [userRole],
  );

  return (
    <div className={css.container}>
      <div className={css.title}>
        <span>
          {translate('listenerList.label')} ({waitingList.length})
        </span>
      </div>
      <div className={css.listeners}>
        <AutoSizer>
          {({ width: w, height: h }: { width: number; height: number }) => {
            const width = w || 0;
            const height = h || 0;

            return (
              <Grid
                className={css.list}
                columnCount={columnsAmount}
                columnWidth={getColumnWidth(width)}
                rowCount={rowsAmount}
                rowHeight={getRowHeight(height)}
                width={width}
                height={height}
                direction="rtl"
              >
                {({ rowIndex, columnIndex, style }) => {
                  const index = getItemIndex(rowIndex, columnIndex);

                  if (waitingList[index]) {
                    return (
                      <React.Fragment key={waitingList[index].uuid}>
                        <Waiter
                          onControlPopUpClick={onItemControlPopUpClose}
                          activeId={activeId}
                          onItemClick={onItemClick}
                          style={style}
                          data={waitingList[index]}
                        />
                      </React.Fragment>
                    );
                  }

                  return null;
                }}
              </Grid>
            );
          }}
        </AutoSizer>
      </div>
    </div>
  );
};

export default React.memo(WaitersList, (prevProps, nextProps) => {
  return _.isEqual(prevProps, nextProps);
});
